import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import Scrollbars from "react-custom-scrollbars-2";
import ImageWithBasePath from "../imageWithBasePath";
import "../../../style/icon/tabler-icons/webfont/tabler-icons.css";
import { setExpandMenu } from "../../data/redux/sidebarSlice";
import { useSelector, useDispatch } from "react-redux";
import { all_routes } from "../../../feature-module/router/all_routes";

import { setDataLayout } from "../../data/redux/themeSettingSlice";
import { SidebarDataTest } from "../../data/json/sidebarMenu";

const Sidebar = () => {
  const Location = useLocation();

  //==========================================================
  // Open CRM in New Tab
  //==========================================================
  const [searchParams] = useSearchParams();
  const [showOnlyCRM, setShowOnlyCRM] = useState(() => {
    return searchParams.get("module") === "crm";
  });

  useEffect(() => {
    setShowOnlyCRM(searchParams.get("module") === "crm");
  }, [searchParams]);

  //==================================================

  const sidebarData = SidebarDataTest();

  const [subOpen, setSubopen] = useState<any>("");
  const [subsidebar, setSubsidebar] = useState("");

  let targetRoute = "/";
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user.user);
  const dataColor = useSelector((state: any) => state.themeSetting.dataColor);
  const dataColorAll = useSelector(
    (state: any) => state.themeSetting.dataColorAll
  );

  const isAll = String(dataColor).toLowerCase() === "all";

  // If you're also storing the picker value in `colorRgb4`, feel free to swap dataColorAll with colorRgb4.

  // Put this in a utils file or top of component
  const toCssColor = (val: any) => {
    if (!val) return undefined;

    if (typeof val === "string") {
      const s = val.trim();

      // Already valid CSS: hex, rgb(), rgba(), or named color
      if (
        /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s) ||
        /^rgba?\(/i.test(s) ||
        /^[a-z]+$/i.test(s)
      )
        return s;

      // "57,83,190" → rgb(57,83,190)
      const parts = s.split(",").map((n) => parseInt(n.trim(), 10));
      if (
        parts.length === 3 &&
        parts.every((n) => Number.isInteger(n) && n >= 0 && n <= 255)
      ) {
        return `rgb(${parts[0]},${parts[1]},${parts[2]})`;
      }
    }

    // [57,83,190] → rgb(...)
    if (Array.isArray(val) && val.length >= 3) {
      const [r, g, b, a] = val;
      return a === 0 || a ? `rgba(${r},${g},${b},${a})` : `rgb(${r},${g},${b})`;
    }

    // {r:57,g:83,b:190[,a:0.8]} → rgb/rgba
    if (typeof val === "object" && val !== null && "r" in val) {
      const { r, g, b, a } = val;
      return a === 0 || a ? `rgba(${r},${g},${b},${a})` : `rgb(${r},${g},${b})`;
    }

    return undefined; // fallback to let CSS ignore invalid values
  };

  const colorMap: any = {
    yellowgreen: "yellowgreen",
    orange: "#fd7e14",
    teal: "#20c997",
    purple: "#6f42c1",
    rose: "#e83e8c",
    gray: "#6c757d",
    // lunargreen: "rgb(57,83,190)", // if you still want this alias
  };

  const resolvedColor = isAll
    ? toCssColor(dataColorAll) // e.g. "57,83,190" / {r,g,b} / [r,g,b] / "rgb(...)"
    : toCssColor(colorMap[dataColor] ?? dataColor);

  const routes = all_routes;

  if (user?.userDetails.role_char.toLowerCase() === "super admin") {
    targetRoute = routes.superAdminDashboard;
  } else if (user?.userDetails.role_char.toLowerCase() === "admin") {
    targetRoute = routes.adminDashboard;
  }

  const toggleSidebar = (title: any) => {
    localStorage.setItem("menuOpened", title);
    if (title === subOpen) {
      setSubopen("");
    } else {
      setSubopen(title);
    }
  };

  const toggleSubsidebar = (subitem: any) => {
    if (subitem === subsidebar) {
      setSubsidebar("");
    } else {
      setSubsidebar(subitem);
    }
  };

  const handleLayoutChange = (layout: string) => {
    dispatch(setDataLayout(layout));
  };

  const handleClick = (
    label: any,
    themeSetting: any,
    layout: any,
    isSubmenu: boolean = false
  ) => {
    if (isSubmenu) {
      toggleSidebar(label);
    }
    if (themeSetting) {
      handleLayoutChange(layout);
    }
  };

  const getLayoutClass = (label: any) => {
    switch (label) {
      case "Default":
        return "default_layout";
      case "Mini":
        return "mini_layout";
      case "Box":
        return "boxed_layout";
      case "Dark":
        return "dark_data_theme";
      case "RTL":
        return "rtl";
      default:
        return "";
    }
  };

  useEffect(() => {
    const currentMenu = localStorage.getItem("menuOpened") || "";
    setSubopen(currentMenu);
    // Select all 'submenu' elements
    const submenus = document.querySelectorAll(".submenu");
    // Loop through each 'submenu'
    submenus.forEach((submenu) => {
      // Find all 'li' elements within the 'submenu'
      const listItems = submenu.querySelectorAll("li");
      submenu.classList.remove("active");
      // Check if any 'li' has the 'active' class
      listItems.forEach((item) => {
        if (item.classList.contains("active")) {
          // Add 'active' class to the 'submenu'
          submenu.classList.add("active");
          return;
        }
      });
    });
  }, [Location.pathname]);

  const onMouseEnter = () => {
    dispatch(setExpandMenu(true));
  };
  const onMouseLeave = () => {
    dispatch(setExpandMenu(false));
  };
  return (
    <>
      <div
        className="sidebar"
        id="sidebar"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="sidebar-logo">
          <Link to={targetRoute} className="logo logo-normal">
            <ImageWithBasePath
              src="assets/img/logo.svg"
              alt="Logo"
              width={100}
            />
          </Link>
          <Link to={targetRoute} className="logo-small">
            <ImageWithBasePath
              src="assets/img/logo.svg"
              alt="Logo"
              width={45}
            />
          </Link>
          <Link to={targetRoute} className="dark-logo logo-normal">
            <ImageWithBasePath
              src="assets/img/logo.svg"
              alt="Logo"
              width={100}
            />
          </Link>
        </div>

        <div className="modern-profile p-3 pb-0">
          <div className="text-center rounded bg-light p-3 mb-4 user-profile">
            <div className="avatar avatar-lg online mb-3">
              <ImageWithBasePath
                src={
                  user?.userDetails.profile_pic == null
                    ? ""
                    : user?.userDetails.profile_pic
                }
                alt="Img"
                className="img-fluid rounded-circle"
              />
            </div>
            <h6 className="fs-12 fw-normal mb-1">
              {user?.userDetails.fname + " " + user?.userDetails.lname}
            </h6>
            <p className="fs-10">{user?.userDetails.role_char}</p>
          </div>
        </div>

        <div className="sidebar-header p-3 pb-0 pt-2">
          <div className="text-center rounded bg-light p-2 mb-4 sidebar-profile d-flex align-items-center">
            <div className="avatar avatar-md onlin">
              <ImageWithBasePath
                src={
                  user?.userDetails.profile_pic == null
                    ? ""
                    : user?.userDetails.profile_pic
                }
                alt="Img"
                className="img-fluid rounded-circle"
              />
            </div>
            <div className="text-start sidebar-profile-info ms-2">
              <h6 className="fs-12 fw-normal mb-1">
                {user?.userDetails.fname + " " + user?.userDetails.lname}
              </h6>
              <p className="fs-10">{user?.userDetails.role_char}</p>
            </div>
          </div>

          {/* <div className="d-flex align-items-center justify-content-between menu-item mb-3">
            <div className="me-3">
              <Link to="#" className="btn btn-menubar position-relative">
                <i className="ti ti-shopping-bag"></i>
                <span className="badge bg-success rounded-pill d-flex align-items-center justify-content-center header-badge">
                  200
                </span>
              </Link>
            </div>
            <div className="me-3">
              <Link to="#" className="btn btn-menubar">
                <i className="ti ti-layout-grid-remove"></i>
              </Link>
            </div>
            <div className="me-3">
              <Link to="#" className="btn btn-menubar position-relative">
                <i className="ti ti-brand-hipchat"></i>
                <span className="badge bg-info rounded-pill d-flex align-items-center justify-content-center header-badge">
                  5
                </span>
              </Link>
            </div>
            <div className="me-3 notification-item">
              <Link to="#" className="btn btn-menubar position-relative me-1">
                <i className="ti ti-bell"></i>
                <span className="notification-status-dot"></span>
              </Link>
            </div>
            <div className="me-0">
              <Link to="#" className="btn btn-menubar">
                <i className="ti ti-message"></i>
              </Link>
            </div>
          </div> */}
        </div>

        <Scrollbars
          autoHide
          renderTrackVertical={(props) => (
            <div {...props} style={{ display: "none" }} />
          )}
          renderThumbVertical={(props) => (
            <div {...props} style={{ display: "none" }} />
          )}
        >
          <div className="sidebar-inner slimscroll">
            <div id="sidebar-menu" className="sidebar-menu">
              <ul>
                {sidebarData?.map((mainLabel, index) => {
                  if (
                    user?.userDetails.menu.some((item: any) => {
                      const isUserMenu =
                        item?.toLowerCase() ===
                        mainLabel?.tittle?.toLowerCase();

                      return showOnlyCRM
                        ? mainLabel?.tittle?.toLocaleLowerCase() == "crm" &&
                            isUserMenu
                        : isUserMenu;
                    })
                  ) {
                    return (
                      <React.Fragment key={`main-${index}`}>
                        <li className="menu-title">
                          <span
                            style={{
                              color: resolvedColor || "inherit",
                              fontWeight: "bold",
                              fontSize: "15px",
                            }}
                          >
                            {mainLabel?.tittle}
                          </span>
                        </li>
                        <li>
                          {
                            <ul>
                              {showOnlyCRM ||
                              mainLabel?.tittle?.toLowerCase() != "crm" ? (
                                mainLabel?.submenuItems?.map(
                                  (title: any, i) => {
                                    if (
                                      user?.userDetails.menu_items.some(
                                        (item: any) =>
                                          item.toLowerCase() ===
                                          title.label.toLowerCase()
                                      )
                                    ) {
                                      let link_array: any = [];
                                      if ("submenuItems" in title) {
                                        title.submenuItems?.forEach(
                                          (link: any) => {
                                            link_array.push(link?.link);
                                            if (
                                              link?.submenu &&
                                              "submenuItems" in link
                                            ) {
                                              link.submenuItems?.forEach(
                                                (item: any) => {
                                                  link_array.push(item?.link);
                                                }
                                              );
                                            }
                                          }
                                        );
                                      }
                                      title.links = link_array;

                                      return (
                                        <li
                                          className="submenu"
                                          key={`title-${i}`}
                                        >
                                          <Link
                                            to={
                                              title?.submenu
                                                ? "#"
                                                : `${title?.link}${
                                                    showOnlyCRM
                                                      ? "?module=crm"
                                                      : ""
                                                  }`
                                            }
                                            onClick={(e) => {
                                              if (title?.submenu) {
                                                e.preventDefault();
                                                handleClick(
                                                  title?.label,
                                                  title?.themeSetting,
                                                  getLayoutClass(title?.label),
                                                  true
                                                );
                                              }
                                            }}
                                            className={`${
                                              subOpen === title?.label
                                                ? "subdrop"
                                                : ""
                                            } ${
                                              title?.links?.includes(
                                                Location.pathname
                                              )
                                                ? "active"
                                                : ""
                                            } ${
                                              title?.submenuItems
                                                ?.map((link: any) => link?.link)
                                                .includes(Location.pathname) ||
                                              title?.link === Location.pathname
                                                ? "active"
                                                : ""
                                            }`}
                                          >
                                            <i
                                              className={`ti ti-${title.icon}`}
                                              style={{
                                                fontSize: "25px",
                                                color:
                                                  resolvedColor || "inherit",
                                              }}
                                            ></i>
                                            <span style={{ fontSize: "15px" }}>
                                              {title?.displayLabel || title?.label}
                                            </span>
                                            {title?.dot && (
                                              <span className="badge badge-danger fs-10 fw-medium text-white p-1">
                                                Hot
                                              </span>
                                            )}
                                            <span
                                              className={
                                                title?.submenu
                                                  ? "menu-arrow"
                                                  : ""
                                              }
                                            />
                                          </Link>
                                          {title?.submenu !== false &&
                                            subOpen === title?.label && (
                                              <ul
                                                style={{
                                                  display:
                                                    subOpen === title?.label
                                                      ? "block"
                                                      : "none",
                                                }}
                                              >
                                                {title?.submenuItems?.map(
                                                  (item: any, j: any) => (
                                                    <li
                                                      className={
                                                        item?.submenuItems
                                                          ? "submenu submenu-two"
                                                          : ""
                                                      }
                                                      key={`item-${j}`}
                                                    >
                                                      <Link
                                                        to={
                                                          item?.submenu
                                                            ? "#"
                                                            : `${item?.link}${
                                                                showOnlyCRM
                                                                  ? "?module=crm"
                                                                  : ""
                                                              }`
                                                        }
                                                        className={`${
                                                          item?.submenuItems
                                                            ?.map(
                                                              (link: any) =>
                                                                link?.link
                                                            )
                                                            .includes(
                                                              Location.pathname
                                                            ) ||
                                                          item?.link ===
                                                            Location.pathname
                                                            ? "active"
                                                            : ""
                                                        } ${
                                                          subsidebar ===
                                                          item?.label
                                                            ? "subdrop"
                                                            : ""
                                                        }`}
                                                        onClick={() => {
                                                          toggleSubsidebar(
                                                            item?.label
                                                          );
                                                        }}
                                                        style={{
                                                          fontSize: "13px",
                                                        }}
                                                      >
                                                        <i
                                                          className={`ti ti-${item.icon}`}
                                                          style={{
                                                            fontSize: "25px",
                                                            color:
                                                              resolvedColor ||
                                                              "inherit",
                                                          }}
                                                        ></i>
                                                        {item?.label}
                                                        {item?.dot && (
                                                          <span className="badge badge-danger fs-15 fw-medium text-white p-1">
                                                            New
                                                          </span>
                                                        )}
                                                        <span
                                                          className={
                                                            item?.submenu
                                                              ? "menu-arrow"
                                                              : ""
                                                          }
                                                        />
                                                      </Link>
                                                      {item?.submenuItems ? (
                                                        <ul
                                                          style={{
                                                            display:
                                                              subsidebar ===
                                                              item?.label
                                                                ? "block"
                                                                : "none",
                                                          }}
                                                        >
                                                          {item?.submenuItems?.map(
                                                            (
                                                              items: any,
                                                              k: any
                                                            ) => (
                                                              <li
                                                                key={`submenu-item-${k}`}
                                                              >
                                                                <Link
                                                                  to={
                                                                    items?.submenu
                                                                      ? "#"
                                                                      : `${
                                                                          items?.link
                                                                        }${
                                                                          showOnlyCRM
                                                                            ? "?module=crm"
                                                                            : ""
                                                                        }`
                                                                  }
                                                                  className={`${
                                                                    subsidebar ===
                                                                    items?.label
                                                                      ? "submenu-two subdrop"
                                                                      : "submenu-two"
                                                                  } ${
                                                                    items?.submenuItems
                                                                      ?.map(
                                                                        (
                                                                          link: any
                                                                        ) =>
                                                                          link.link
                                                                      )
                                                                      .includes(
                                                                        Location.pathname
                                                                      ) ||
                                                                    items?.link ===
                                                                      Location.pathname
                                                                      ? "active"
                                                                      : ""
                                                                  }`}
                                                                >
                                                                  {items?.label}
                                                                </Link>
                                                              </li>
                                                            )
                                                          )}
                                                        </ul>
                                                      ) : null}
                                                    </li>
                                                  )
                                                )}
                                              </ul>
                                            )}
                                        </li>
                                      );
                                    }
                                  }
                                )
                              ) : (
                                <li className="submenu" key={`title-open-crm`}>
                                  <Link
                                    to={`${all_routes?.leadsGrid}?module=crm`}
                                    target="_blank"
                                  >
                                    <i
                                      className={`ti ti-external-link`}
                                      style={{
                                        fontSize: "25px",
                                        color: resolvedColor || "inherit",
                                      }}
                                    ></i>
                                    <span style={{ fontSize: "15px" }}>
                                      Open CRM (New Tab)
                                    </span>
                                  </Link>
                                </li>
                              )}
                              {/* {showOnlyCRM && (
                                <li className="submenu" key={`title-show-all`}>
                                  <Link to={``}>
                                    <i
                                      className={`ti ti-layout-grid`}
                                      style={{
                                        fontSize: "25px",
                                        color: resolvedColor || "inherit",
                                      }}></i>
                                    <span style={{ fontSize: "15px" }}>
                                      Show All Modules
                                    </span>
                                  </Link>
                                </li>
                              )} */}
                            </ul>
                          }
                        </li>
                      </React.Fragment>
                    );
                  }
                })}
              </ul>
            </div>
          </div>
        </Scrollbars>
      </div>
    </>
  );
};

export default Sidebar;
